/**
 * UNIFIED AUTHENTICATION CONTEXT
 * 
 * Auth0 performs the hosted identity ceremony for Google and OAuth login.
 * BotSpot custom JWTs are the only application sessions accepted by this
 * provider. An Auth0 identity must be exchanged for a BotSpot JWT before the
 * application reports an authenticated user or exposes an API token.
 * 
 * AUTHENTICATION FLOW AFTER PURCHASE:
 * 1. User completes Stripe payment
 * 2. Backend generates custom JWT token with 15-minute expiry
 * 3. Frontend receives auto-login URL with JWT in query params
 * 4. Frontend extracts JWT, validates it, and sets up auth state
 * 5. User is immediately logged in without Auth0 interaction
 * 
 * JWT STRUCTURE:
 * - userId: Internal user UUID
 * - email: User's email address
 * - auth0UserId: Corresponding Auth0 user ID (for data consistency)
 * - purpose: 'auto_login_after_purchase'
 * - iat: Issued at timestamp
 * - exp: Expires in 15 minutes (security)
 * 
 * APPLICATION API TOKEN SYSTEM:
 * - BotSpot APIs receive only a BotSpot custom JWT
 * - Auth0 access tokens are never application-session fallbacks
 * - Frontend automatically includes the BotSpot token in API requests
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  safeGetStorageItem,
  safeRemoveStorageItem,
  setCriticalStorageItem,
} from '../utils/browserStorage';
import { getAuth0RedirectOrigin } from '../utils/auth0Origin';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import {
  clearHighRiskStepUpActionId,
  readHighRiskStepUpActionId,
} from '../utils/highRiskStepUpStorage';
import { resetAnalyticsIdentity } from '../utils/analytics';
import {
  completePostSignupJourney,
  peekPendingPostSignupDestination,
  startPostSignupJourney,
  takePendingPostSignupDestination,
} from '../utils/postSignupJourney';
import { buildGoogleSearchLeadAttribution } from '../utils/googleSearchLeadAttribution';

const UnifiedAuthContext = createContext();
const AUTH0_SESSION_EXCHANGE_TIMEOUT_MS = 15000;
const AUTH0_ID_TOKEN_EXPIRY_SKEW_SECONDS = 60;
const AUTH0_REAUTH_REQUIRED_CODE = 'AUTH0_REAUTH_REQUIRED';
const IDLE_AUTH0_SESSION_EXCHANGE_STATE = {
  isLoading: false,
  error: null,
  requiresReauth: false
};

const isFreshAuth0IdToken = (claims) => {
  const expiresAt = Number(claims?.exp);
  const now = Math.floor(Date.now() / 1000);
  return Number.isFinite(expiresAt) && expiresAt > now + AUTH0_ID_TOKEN_EXPIRY_SKEW_SECONDS;
};

const auth0ReauthRequiredError = (message) => {
  const error = new Error(message || 'Your sign-in session expired. Please sign in again.');
  error.code = AUTH0_REAUTH_REQUIRED_CODE;
  return error;
};

const decodeJwtPayload = (token) => {
  const encoded = token.split('.')[1];
  if (!encoded) return null;
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return JSON.parse(atob(padded));
};

const embeddedAuthEndpoint = (path) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  return `${apiBase || ''}/auth/embedded/${path}`;
};

const CUSTOM_JWT_VERIFY_ACCEPTED = 'accepted';
const CUSTOM_JWT_VERIFY_REJECTED = 'rejected';
const CUSTOM_JWT_VERIFY_TRANSPORT_FAILURE = 'transport_failure';

const verifyCustomJwtWithBackend = async (token) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  if (!apiBase) {
    console.error('[UnifiedAuth] API URL is missing; refusing custom JWT authentication');
    return CUSTOM_JWT_VERIFY_REJECTED;
  }

  try {
    const response = await fetchWithTimeout(`${apiBase}/users/user_profile`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.ok) {
      const body = await response.json();
      const accepted = Boolean(
        body?.success === true &&
        typeof body?.profile?.id === 'string' &&
        body.profile.id.length > 0
      );
      return accepted ? CUSTOM_JWT_VERIFY_ACCEPTED : CUSTOM_JWT_VERIFY_REJECTED;
    }
    if (response.status === 401 || response.status === 403) {
      return CUSTOM_JWT_VERIFY_REJECTED;
    }
    console.warn('[UnifiedAuth] Custom JWT backend verification returned a transient status:', response.status);
    return CUSTOM_JWT_VERIFY_TRANSPORT_FAILURE;
  } catch (error) {
    console.warn('[UnifiedAuth] Custom JWT backend verification failed:', error?.message || 'unknown error');
    return CUSTOM_JWT_VERIFY_TRANSPORT_FAILURE;
  }
};

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  }
  return context;
};

export const UnifiedAuthProvider = ({ children }) => {
  // Auth0 authentication state
  const { 
    user: auth0User, 
    isAuthenticated: auth0IsAuthenticated, 
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect: auth0LoginWithRedirect,
    logout: auth0Logout
  } = useAuth0();

  const [auth0StartupTimedOut, setAuth0StartupTimedOut] = useState(false);

  // Custom JWT authentication state
  const [customJwtState, setCustomJwtState] = useState({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false
  });
  const [hasCheckedStoredCustomJwt, setHasCheckedStoredCustomJwt] = useState(false);
  const activeCustomJwtTokenRef = useRef(null);
  const handledUrlCustomJwtTokenRef = useRef(null);
  const inFlightCustomJwtAuthRef = useRef(null);
  const customJwtIsAuthenticatedRef = useRef(false);
  const auth0SessionExchangeKeyRef = useRef(null);
  const auth0SessionExchangeFailureActiveRef = useRef(false);
  // An embedded-email recovery can supersede a failed hosted exchange while
  // Auth0 still reports the old identity. Keep that identity terminally
  // anonymous instead of treating it as a new exchange that is loading forever.
  const auth0SessionExchangeFailureSupersededRef = useRef(false);
  const logoutInProgressRef = useRef(false);
  const [auth0SessionExchangeState, setAuth0SessionExchangeState] = useState(
    IDLE_AUTH0_SESSION_EXCHANGE_STATE
  );

  const clearAuthSessionExchangeFailure = useCallback(() => {
    if (!auth0SessionExchangeFailureActiveRef.current) return;
    auth0SessionExchangeFailureActiveRef.current = false;
    auth0SessionExchangeFailureSupersededRef.current = true;
    setAuth0SessionExchangeState(IDLE_AUTH0_SESSION_EXCHANGE_STATE);
  }, []);

  useEffect(() => {
    customJwtIsAuthenticatedRef.current = customJwtState.isAuthenticated;
  }, [customJwtState.isAuthenticated]);

  // Unified authentication state
  const [unifiedAuth, setUnifiedAuth] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    authMethod: null, // null | 'custom_jwt'
    token: null
  });

  useEffect(() => {
    if (!auth0IsLoading) {
      setAuth0StartupTimedOut(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      console.warn('[UnifiedAuth] Auth0 startup exceeded timeout; continuing without blocking app render');
      setAuth0StartupTimedOut(true);
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [auth0IsLoading]);

  /**
   * CUSTOM JWT TOKEN VALIDATION AND DECODING
   * Validates JWT structure and expiry, returns decoded payload or null
   */
  const validateCustomJwt = useCallback((token) => {
    try {
      if (!token || typeof token !== 'string') {
        console.log('[UnifiedAuth] Invalid token format');
        return null;
      }

      // Basic JWT structure validation (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('[UnifiedAuth] Invalid JWT structure');
        return null;
      }

      // JWT payloads use base64url, not plain base64. Normalize URL-safe
      // characters and padding before decoding valid backend-issued sessions.
      const payload = decodeJwtPayload(token);
      if (!payload) return null;
      
      // Validate required fields
      if (!payload.userId || !payload.email || !payload.purpose) {
        console.log('[UnifiedAuth] Missing required JWT fields');
        return null;
      }

      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.log('[UnifiedAuth] JWT token has expired');
        return null;
      }

      // Validate purpose (security check)
      // Accept both post-purchase tokens and lead auto-login tokens
      const validPurposes = ['auto_login_after_purchase', 'lead_auto_login', 'embedded_auth0_email'];
      if (!validPurposes.includes(payload.purpose)) {
        console.log('[UnifiedAuth] Invalid JWT purpose');
        return null;
      }

      console.debug('[UnifiedAuth] JWT validation successful');

      return payload;
    } catch (error) {
      console.error('[UnifiedAuth] JWT validation error:', error?.message || 'unknown error');
      return null;
    }
  }, []);

  /**
   * CUSTOM JWT AUTHENTICATION HANDLER
   * Processes custom JWT from URL parameters or localStorage
   */
  const handleCustomJwtAuth = useCallback(async (token) => {
    if (logoutInProgressRef.current) {
      console.debug('[UnifiedAuth] Ignoring custom JWT while logout is in progress');
      return false;
    }

    if (inFlightCustomJwtAuthRef.current?.token === token) {
      return inFlightCustomJwtAuthRef.current.promise;
    }

    if (activeCustomJwtTokenRef.current === token) {
      console.debug('[UnifiedAuth] Custom JWT already active; skipping duplicate state update');
      return true;
    }

    const authenticate = async () => {
      console.log('[UnifiedAuth] Processing custom JWT authentication');
      const payload = validateCustomJwt(token);
      if (!payload) {
        if (activeCustomJwtTokenRef.current === token) {
          activeCustomJwtTokenRef.current = null;
        }
        console.log('[UnifiedAuth] Custom JWT validation failed');
        return false;
      }

      // Verifying a URL or stored BotSpot session is part of authentication,
      // not an anonymous idle state. Keep protected routes in their loading
      // state until verification finishes so they cannot redirect to login or
      // checkout during the async profile round trip. This is especially
      // important under React StrictMode, where the URL-cleanup effect can be
      // remounted after it removes autoLoginToken but before verification has
      // committed the authenticated session.
      setCustomJwtState((current) => ({
        ...current,
        isLoading: true,
      }));

      if (activeCustomJwtTokenRef.current === token) {
        console.debug('[UnifiedAuth] Custom JWT already active; skipping duplicate state update');
        return true;
      }

      const verification = await verifyCustomJwtWithBackend(token);
      if (verification === CUSTOM_JWT_VERIFY_REJECTED) {
        if (activeCustomJwtTokenRef.current === token) {
          activeCustomJwtTokenRef.current = null;
        }
        if (safeGetStorageItem('localStorage', 'customAuthToken') === token) {
          safeRemoveStorageItem('localStorage', 'customAuthToken');
        }
        setCustomJwtState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
        console.log('[UnifiedAuth] Custom JWT rejected by backend');
        return false;
      }

      if (verification === CUSTOM_JWT_VERIFY_TRANSPORT_FAILURE) {
        console.warn('[UnifiedAuth] Keeping locally valid BotSpot session after profile transport failure');
      }

      try {
        // Repeated SPA location changes can find the same stored token many times.
        // Treat one accepted token as idempotent so auth/payment routing effects do
        // not churn while checkout is trying to mount.
        activeCustomJwtTokenRef.current = token;

        // Store token for API requests. This is a critical auth write, so recover
        // from quota pressure caused by derived chart/indicator caches before
        // accepting the login as durable.
        const tokenStored = setCriticalStorageItem('localStorage', 'customAuthToken', token);
        if (!tokenStored) {
          throw new Error('Unable to persist BotSpot session token.');
        }

        setCustomJwtState({
          user: {
            id: payload.userId,
            email: payload.email,
            auth0Sub: payload.auth0UserId || null,
          },
          token: token,
          isAuthenticated: true,
          isLoading: false
        });
        clearAuthSessionExchangeFailure();

        console.log('[UnifiedAuth] Custom JWT authentication successful');
        return true;
      } catch (error) {
        if (activeCustomJwtTokenRef.current === token) {
          activeCustomJwtTokenRef.current = null;
        }
        setCustomJwtState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
        console.error('[UnifiedAuth] Custom JWT authentication error:', error?.message || 'unknown error');
        return false;
      }
    };

    const promise = authenticate();
    inFlightCustomJwtAuthRef.current = { token, promise };
    try {
      return await promise;
    } finally {
      if (inFlightCustomJwtAuthRef.current?.promise === promise) {
        inFlightCustomJwtAuthRef.current = null;
      }
    }
  }, [clearAuthSessionExchangeFailure, validateCustomJwt]);

  const getAuth0SessionAccessToken = useCallback(async (options = {}) => {
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    const authorizationParams = {
      scope: 'openid profile email',
      ...(audience ? { audience } : {})
    };

    return getAccessTokenSilently({
      ...(options.cacheMode ? { cacheMode: options.cacheMode } : {}),
      authorizationParams
    });
  }, [getAccessTokenSilently]);

  /**
   * TOKEN RETRIEVAL FOR API REQUESTS
   * Returns the BotSpot application-session token.
   */
  const getApiToken = useCallback(async () => {
    console.debug('[UnifiedAuth] getApiToken called:', {
      authMethod: unifiedAuth.authMethod,
      hasCustomToken: !!customJwtState.token,
      isAuthenticated: unifiedAuth.isAuthenticated,
      auth0IsAuthenticated
    });

    if (customJwtState.token && (unifiedAuth.authMethod === 'custom_jwt' || customJwtState.isAuthenticated)) {
      console.debug('[UnifiedAuth] Returning custom JWT token');
      return customJwtState.token;
    }

    console.warn('[UnifiedAuth] No authentication method available, returning null');
    return null;
  }, [
    unifiedAuth.authMethod,
    unifiedAuth.isAuthenticated,
    customJwtState.token,
    customJwtState.isAuthenticated,
    auth0IsAuthenticated
  ]);

  const getFreshLoginIdToken = useCallback(async () => {
    if (!auth0IsAuthenticated || typeof getIdTokenClaims !== 'function') {
      return null;
    }
    try {
      const claims = await getIdTokenClaims();
      return claims?.__raw || null;
    } catch (error) {
      console.warn('[UnifiedAuth] Failed to get Auth0 ID token claims for fresh-login proof:', error?.message || 'unknown error');
      return null;
    }
  }, [auth0IsAuthenticated, getIdTokenClaims]);

  const loginWithRedirect = useCallback((options = {}) => {
    logoutInProgressRef.current = false;

    const authorizationParams = {
      ...(options?.authorizationParams || {})
    };

    // Normal login should let users switch accounts after logout instead of
    // silently reusing the browser's last social-provider session. Security
    // sensitive callers can still pass prompt: 'login' to force fresh auth.
    if (!authorizationParams.prompt) {
      authorizationParams.prompt = 'select_account';
    }

    return auth0LoginWithRedirect({
      ...options,
      authorizationParams
    });
  }, [auth0LoginWithRedirect]);

  /**
   * UNIFIED LOGOUT FUNCTION
   * Clears both Auth0 and custom JWT authentication
   */
  const logout = useCallback(async (options = {}) => {
    console.log('[UnifiedAuth] Logging out from unified auth system');
    resetAnalyticsIdentity();
    const hasAuth0SessionAtLogout = auth0IsAuthenticated;
    logoutInProgressRef.current = true;

    const tokenTimeout = new Promise((resolve) => {
      setTimeout(() => resolve(null), 1500);
    });
    
    try {
      // Call backend logout endpoint if we have a token
      const token = await Promise.race([getApiToken(), tokenTimeout]);
      if (token) {
        console.log('[UnifiedAuth] Calling backend logout endpoint');
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('[UnifiedAuth] Backend logout successful:', result);
          } else {
            console.warn('[UnifiedAuth] Backend logout failed, but continuing with client-side logout');
          }
        } catch (backendError) {
          console.warn('[UnifiedAuth] Backend logout request failed:', backendError);
          // Continue with client-side logout even if backend fails
        }
      }
    } catch (tokenError) {
      console.log('[UnifiedAuth] No token available for backend logout');
    }
    
    // Immediately clear unified auth state to prevent any lingering authenticated state
    activeCustomJwtTokenRef.current = null;
    handledUrlCustomJwtTokenRef.current = null;
    auth0SessionExchangeKeyRef.current = null;
    auth0SessionExchangeFailureActiveRef.current = false;
    auth0SessionExchangeFailureSupersededRef.current = false;
    setAuth0SessionExchangeState(IDLE_AUTH0_SESSION_EXCHANGE_STATE);
    setUnifiedAuth({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authMethod: null,
      token: null
    });
    
    // Clear custom JWT state
    setCustomJwtState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false
    });
    
    // Clear stored custom token
    safeRemoveStorageItem('localStorage', 'customAuthToken');
    
    // Clear any other auth-related data from localStorage
    safeRemoveStorageItem('localStorage', 'auth0.token');
    safeRemoveStorageItem('localStorage', 'auth0.access_token');
    safeRemoveStorageItem('localStorage', 'auth0.id_token');
    
    // CRITICAL FIX: Clear course registration data on logout
    // This prevents registration data from persisting across different user sessions
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('ai-bootcamp-registered-') || 
          key.startsWith('edu-workshop-registered-') ||
          key.includes('course-registered-')) {
        console.log('[UnifiedAuth] Clearing course registration data:', key);
        safeRemoveStorageItem('localStorage', key);
      }
    });
    
    // Clear session storage as well
    sessionStorage.clear();
    
    console.log('[UnifiedAuth] All auth state and storage cleared');
    
    // Logout from Auth0 whenever its browser session is present. Auth0 social
    // logins are exchanged into BotSpot custom JWTs after login, so checking
    // only the active unified auth method would leave the Auth0 session alive
    // and recreate the BotSpot session immediately after local cleanup.
    if (hasAuth0SessionAtLogout) {
      console.log('[UnifiedAuth] Logging out via Auth0');
      const requestedLogoutParams = options?.logoutParams || {};
      try {
        auth0Logout({
          ...options,
          logoutParams: {
            returnTo: getAuth0RedirectOrigin(window.location),
            ...requestedLogoutParams
          }
        });
      } catch (auth0LogoutError) {
        console.warn('[UnifiedAuth] Auth0 logout failed after local cleanup; redirecting home', auth0LogoutError);
        window.location.href = '/';
      }
    } else {
      // For custom JWT or any other case, redirect to home page
      console.log('[UnifiedAuth] Redirecting to home page');
      // Use setTimeout to ensure state updates are processed before redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
  }, [auth0Logout, auth0IsAuthenticated, getApiToken]);

  /**
   * AUTO-LOGIN TOKEN DETECTION
   *
   * Checks URL parameters for an autoLoginToken on every location change, not
   * just on provider mount. The provider lives above the Router so it can't
   * use useLocation; instead we patch history.pushState/replaceState to emit
   * a 'locationchange' event, and subscribe to that plus the native popstate.
   *
   * Why: the sales funnel SPA-navigates from /sales -> /sales-choose carrying
   * the autoLoginToken in the URL. Before this fix, the mount-only effect
   * never saw the token because the provider doesn't remount across SPA
   * transitions, leaving users unauthenticated at /sales-choose. A direct
   * handleCustomJwtAuth(token) call from the lead-submit handler also racing
   * with SalesPage's auth-redirect useEffect caused the user to get shoved
   * into /ai-agent (paywall) instead of /sales-choose, so we listen at the
   * URL level instead and stay out of the submit handler's way.
   */
  useEffect(() => {
    let cancelled = false;

    const checkUrlForAutoLoginToken = async () => {
      if (logoutInProgressRef.current) {
        console.debug('[UnifiedAuth] Skipping auto-login token check while logout is in progress');
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const autoLoginToken = urlParams.get('autoLoginToken');

      if (autoLoginToken) {
        if (handledUrlCustomJwtTokenRef.current === autoLoginToken) {
          return;
        }
        handledUrlCustomJwtTokenRef.current = autoLoginToken;
        console.log('[UnifiedAuth] Auto-login token detected in URL');

        // Remove token from URL for security (avoids leaking in referrer/history)
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('autoLoginToken');
        // Pass an explicit same-origin relative string. Firefox can retain the
        // original query when a URL object crosses the patched history wrapper,
        // which leaves the signed login token visible even after authentication.
        const sanitizedLocation = `${newUrl.pathname}${newUrl.search}${newUrl.hash}`;
        window.history.replaceState({}, '', sanitizedLocation);
        // BrowserRouter listens to popstate. Keep its location snapshot in sync
        // so later tab/query updates cannot reintroduce the removed JWT.
        window.dispatchEvent(new PopStateEvent('popstate'));

        const accepted = await handleCustomJwtAuth(autoLoginToken);

        // BrowserRouter can commit a stale pre-auth location during the same
        // render in which authentication settles. Fail closed on token
        // exposure: if that race restored the signed token, replace the
        // document with the already-sanitized location. The stored session
        // survives this same-origin reload.
        const tokenWasRestored = new URLSearchParams(window.location.search)
          .get('autoLoginToken') !== null;
        if (accepted && tokenWasRestored) {
          window.location.replace(sanitizedLocation);
        }
      } else {
        const storedToken = safeGetStorageItem('localStorage', 'customAuthToken');
        if (storedToken && activeCustomJwtTokenRef.current !== storedToken) {
          console.log('[UnifiedAuth] Stored custom token found');
          await handleCustomJwtAuth(storedToken);
        }
      }
    };

    // Patch history methods once to emit a locationchange event on SPA nav.
    // Guarded with a window-level flag so React StrictMode double-mounts don't
    // double-patch and so other consumers (like the react-router internals)
    // keep working.
    if (typeof window !== 'undefined' && !window.__unifiedAuthLocationPatch) {
      window.__unifiedAuthLocationPatch = true;
      const originalPush = window.history.pushState;
      const originalReplace = window.history.replaceState;
      window.history.pushState = function (...args) {
        const result = originalPush.apply(this, args);
        window.dispatchEvent(new Event('locationchange'));
        return result;
      };
      window.history.replaceState = function (...args) {
        const result = originalReplace.apply(this, args);
        window.dispatchEvent(new Event('locationchange'));
        return result;
      };
    }

    // Initial check on mount. Keep auth loading until this finishes so a fresh
    // tab cannot redirect to /login before the saved BotSpot session is applied.
    checkUrlForAutoLoginToken().finally(() => {
      if (!cancelled) {
        setHasCheckedStoredCustomJwt(true);
      }
    });

    // Re-check on every location change (popstate = back/forward, locationchange = pushState/replaceState)
    const handler = () => {
      void checkUrlForAutoLoginToken();
    };
    window.addEventListener('popstate', handler);
    window.addEventListener('locationchange', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('popstate', handler);
      window.removeEventListener('locationchange', handler);
    };
  }, [handleCustomJwtAuth]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== 'customAuthToken' || logoutInProgressRef.current) {
        return;
      }

      if (event.newValue) {
        void handleCustomJwtAuth(event.newValue);
        return;
      }

      activeCustomJwtTokenRef.current = null;
      handledUrlCustomJwtTokenRef.current = null;
      setCustomJwtState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      });
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleCustomJwtAuth]);

  /**
   * MEMOIZED AUTH0 USER OBJECT
   * Prevents unnecessary re-renders by maintaining stable object identity
   * Only creates a new object when actual user values change
   */
  const memoizedAuth0User = useMemo(() => {
    if (!auth0IsAuthenticated || !auth0User) return null;

    // Note: email_verified is a boolean indicating if email is verified, not the email itself
    const userEmail = auth0User.email || null;
    return {
      id: auth0User.sub,
      email: userEmail,
      name: auth0User.name,
      auth0Sub: auth0User.sub,
    };
  }, [auth0IsAuthenticated, auth0User?.sub, auth0User?.email, auth0User?.name]);

  useEffect(() => {
    if (!auth0IsAuthenticated) {
      auth0SessionExchangeKeyRef.current = null;
      auth0SessionExchangeFailureActiveRef.current = false;
      auth0SessionExchangeFailureSupersededRef.current = false;
      setAuth0SessionExchangeState(IDLE_AUTH0_SESSION_EXCHANGE_STATE);
      return;
    }

    if (logoutInProgressRef.current) {
      return;
    }

    const highRiskStepUpActionId = readHighRiskStepUpActionId();

    if (
      !memoizedAuth0User ||
      (customJwtIsAuthenticatedRef.current && !highRiskStepUpActionId) ||
      typeof getIdTokenClaims !== 'function'
    ) {
      return;
    }

    const baseExchangeKey = memoizedAuth0User.auth0Sub || memoizedAuth0User.id;
    const exchangeKey = highRiskStepUpActionId
      ? `${baseExchangeKey}:high-risk-step-up:${highRiskStepUpActionId}`
      : baseExchangeKey;
    if (!exchangeKey || auth0SessionExchangeKeyRef.current === exchangeKey) {
      return;
    }

    let cancelled = false;
    auth0SessionExchangeKeyRef.current = exchangeKey;
    auth0SessionExchangeFailureActiveRef.current = false;
    auth0SessionExchangeFailureSupersededRef.current = false;
    setAuth0SessionExchangeState({ isLoading: true, error: null, requiresReauth: false });

    const exchangeAuth0Session = async () => {
      let postSignupJourneyStarted = false;
      if (logoutInProgressRef.current) {
        return;
      }

      try {
        const pendingPostSignupDestination = peekPendingPostSignupDestination();
        const googleSearchLeadAttribution = buildGoogleSearchLeadAttribution({
          destination: pendingPostSignupDestination,
        });
        let accessToken = null;
        let claims = await getIdTokenClaims();
        if (cancelled) {
          return;
        }
        if (!isFreshAuth0IdToken(claims)) {
          try {
            accessToken = await getAuth0SessionAccessToken({ cacheMode: 'off' });
          } catch (refreshError) {
            throw auth0ReauthRequiredError(
              refreshError?.message || 'Auth0 could not refresh the expired sign-in session.'
            );
          }
          claims = await getIdTokenClaims();
          if (cancelled) {
            return;
          }
        }

        if (!isFreshAuth0IdToken(claims)) {
          throw auth0ReauthRequiredError('Auth0 returned an expired sign-in session.');
        }

        const idToken = claims?.__raw;
        if (!idToken) {
          throw auth0ReauthRequiredError('Auth0 ID token was unavailable.');
        }

        if (!accessToken) {
          try {
            accessToken = await getAuth0SessionAccessToken();
          } catch (accessTokenError) {
            throw auth0ReauthRequiredError(
              accessTokenError?.message || 'Auth0 access token was unavailable.'
            );
          }
          if (cancelled) {
            return;
          }
        }
        if (!accessToken) {
          throw auth0ReauthRequiredError('Auth0 access token was unavailable.');
        }

        const requestSessionExchange = async (token) => {
          const response = await fetchWithTimeout(
            embeddedAuthEndpoint('auth0-session'),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                idToken,
                ...(highRiskStepUpActionId ? { highRiskStepUpActionId } : {}),
                ...(googleSearchLeadAttribution
                  ? { leadAttribution: googleSearchLeadAttribution }
                  : {}),
              })
            },
            AUTH0_SESSION_EXCHANGE_TIMEOUT_MS
          );
          return {
            response,
            data: await response.json().catch(() => ({}))
          };
        };

        let { response, data } = await requestSessionExchange(accessToken);
        if (
          response.status === 401 &&
          ['AUTH0_ACCESS_TOKEN_INVALID', 'AUTH0_PROFILE_INVALID'].includes(data?.code)
        ) {
          accessToken = await getAuth0SessionAccessToken({ cacheMode: 'off' });
          if (!accessToken) {
            throw auth0ReauthRequiredError('Auth0 access token was unavailable.');
          }
          ({ response, data } = await requestSessionExchange(accessToken));
        }

        if (!response.ok || !data?.token) {
          if (response.status === 401 || data?.code === 'AUTH0_ID_TOKEN_INVALID') {
            throw auth0ReauthRequiredError(data?.message);
          }
          throw new Error(data?.message || 'Auth0 session exchange failed.');
        }

        if (cancelled) {
          return;
        }

        if (logoutInProgressRef.current) {
          return;
        }

        const resolvedPostSignupDestination = takePendingPostSignupDestination();
        if (data.postSignupJourneyRequired === true && data.isNewUser === true) {
          postSignupJourneyStarted = startPostSignupJourney(
            data.user?.id,
            'auth0_signup',
            resolvedPostSignupDestination,
          );
        }
        const accepted = await handleCustomJwtAuth(data.token);
        if (!accepted) {
          throw new Error('BotSpot session token was rejected.');
        }
        postSignupJourneyStarted = false;
        if (highRiskStepUpActionId) {
          clearHighRiskStepUpActionId(highRiskStepUpActionId);
        }

        auth0SessionExchangeFailureActiveRef.current = false;
        setAuth0SessionExchangeState(IDLE_AUTH0_SESSION_EXCHANGE_STATE);
      } catch (error) {
        if (postSignupJourneyStarted) {
          completePostSignupJourney();
        }
        if (cancelled) {
          return;
        }

        if (auth0SessionExchangeKeyRef.current === exchangeKey) {
          auth0SessionExchangeKeyRef.current = null;
        }

        console.warn('[UnifiedAuth] Auth0 session exchange failed and requires sign in:', error);
        auth0SessionExchangeFailureActiveRef.current = true;
        setAuth0SessionExchangeState({
          isLoading: false,
          error: error?.message || 'Auth0 session exchange failed.',
          requiresReauth: true
        });
      }
    };

    exchangeAuth0Session();

    return () => {
      cancelled = true;
      // Auth0 can replace its SDK callbacks while settling a redirect. That
      // cancels this effect, but the same identity still needs a fresh attempt.
      // Keeping the key here strands the user in anonymous state because the
      // replacement effect treats the cancelled exchange as completed.
      if (auth0SessionExchangeKeyRef.current === exchangeKey) {
        auth0SessionExchangeKeyRef.current = null;
      }
    };
  }, [
    auth0IsAuthenticated,
    memoizedAuth0User,
    getIdTokenClaims,
    getAuth0SessionAccessToken,
    handleCustomJwtAuth
  ]);

  /**
   * UNIFIED STATE MANAGEMENT
   * Combines Auth0 and custom JWT states into unified state
   * Uses memoized user objects to prevent re-render cascades
   */
  useEffect(() => {
    let newUnifiedState;
    const effectiveAuth0IsLoading = auth0IsLoading && !auth0StartupTimedOut;
    const auth0ExchangeKey = memoizedAuth0User?.auth0Sub || memoizedAuth0User?.id || null;
    const auth0SessionExchangeFailureSuperseded =
      auth0SessionExchangeFailureSupersededRef.current;
    const pendingHighRiskStepUpActionId =
      auth0SessionExchangeState.error || auth0SessionExchangeFailureSuperseded
      ? null
      : readHighRiskStepUpActionId();
    const auth0SessionExchangeInProgress =
      !auth0SessionExchangeFailureSuperseded && (
        auth0SessionExchangeState.isLoading ||
        (
          auth0IsAuthenticated &&
          Boolean(pendingHighRiskStepUpActionId)
        ) ||
        (
          auth0IsAuthenticated &&
          Boolean(auth0ExchangeKey) &&
          typeof getIdTokenClaims === 'function' &&
          auth0SessionExchangeKeyRef.current === auth0ExchangeKey &&
          !auth0SessionExchangeState.error &&
          !customJwtState.isAuthenticated
        )
      );

    // Keep logout terminal on the client until Auth0/custom-JWT redirect
    // finishes. Otherwise the still-live Auth0 SDK state can briefly rebuild
    // an authenticated app session while the browser is leaving the page.
    if (logoutInProgressRef.current) {
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authMethod: null,
        token: null
      };
    } else if (auth0SessionExchangeInProgress) {
      newUnifiedState = {
        user: memoizedAuth0User,
        isAuthenticated: false,
        isLoading: true,
        authMethod: null,
        token: null
      };
    } else if (customJwtState.isAuthenticated) {
      // Priority: Custom JWT over Auth0 (post-purchase flow)
      newUnifiedState = {
        user: customJwtState.user,
        isAuthenticated: true,
        isLoading: false,
        authMethod: 'custom_jwt',
        token: customJwtState.token
      };
    } else if (
      auth0SessionExchangeState.requiresReauth ||
      auth0SessionExchangeFailureSuperseded
    ) {
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authMethod: null,
        token: null
      };
    } else if (auth0IsAuthenticated) {
      const canExchangeAuth0Identity =
        Boolean(memoizedAuth0User) && typeof getIdTokenClaims === 'function';
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: canExchangeAuth0Identity,
        authMethod: null,
        token: null
      };
    } else {
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: effectiveAuth0IsLoading || customJwtState.isLoading || !hasCheckedStoredCustomJwt,
        authMethod: null,
        token: null
      };
    }

    setUnifiedAuth(newUnifiedState);

    console.debug('[UnifiedAuth] Unified state updated:', {
      isAuthenticated: newUnifiedState.isAuthenticated,
      authMethod: newUnifiedState.authMethod
    });
  }, [
    auth0IsAuthenticated,
    memoizedAuth0User,
    auth0IsLoading,
    auth0StartupTimedOut,
    customJwtState,
    hasCheckedStoredCustomJwt,
    auth0SessionExchangeState.isLoading,
    auth0SessionExchangeState.error,
    auth0SessionExchangeState.requiresReauth,
    getIdTokenClaims
  ]);

  /**
   * CUSTOM JWT TOKEN CLEANUP
   * Removes expired tokens from localStorage
   */
  useEffect(() => {
    const cleanupExpiredTokens = () => {
      const storedToken = safeGetStorageItem('localStorage', 'customAuthToken');
      if (storedToken && !validateCustomJwt(storedToken)) {
        console.log('[UnifiedAuth] Removing expired custom token');
        if (activeCustomJwtTokenRef.current === storedToken) {
          activeCustomJwtTokenRef.current = null;
        }
        handledUrlCustomJwtTokenRef.current = null;
        safeRemoveStorageItem('localStorage', 'customAuthToken');
        setCustomJwtState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    // Check for expired tokens on mount and every 5 minutes
    cleanupExpiredTokens();
    const interval = setInterval(cleanupExpiredTokens, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [validateCustomJwt]);

  const contextValue = {
    // Unified state
    user: unifiedAuth.user,
    isAuthenticated: unifiedAuth.isAuthenticated,
    isLoading: unifiedAuth.isLoading,
    authMethod: unifiedAuth.authMethod,
    authSessionExchangeFailed: auth0SessionExchangeState.requiresReauth,
    
    // API integration
    getApiToken,
    getFreshLoginIdToken,
    
    // Authentication actions
    loginWithRedirect, // Auth0 login
    logout,
    clearAuthSessionExchangeFailure,
    
    // Custom JWT utilities
    handleCustomJwtAuth,
    validateCustomJwt,
    
    // Raw states for debugging
    auth0State: { user: auth0User, isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading, startupTimedOut: auth0StartupTimedOut },
    customJwtState
  };

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

export default UnifiedAuthProvider;
